import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CreditCard, Users, Receipt, Wallet, FileArrowUp, Key, ArrowUpRight, ShieldCheck, Gift } from "@phosphor-icons/react";
import MerchantLayout from "@/components/MerchantLayout";
import { StatusPill } from "@/components/Layout";
import SecurityPosture from "@/components/SecurityPosture";
import { mApi, mAuth } from "@/lib/api";
import { MERCHANT } from "@/lib/routes";
import { merchantCardsApi } from "@/lib/merchantCards";

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function graceUsageSubtext(g) {
  const used = g.free_slots_used ?? Math.min(g.cards_used ?? 0, g.free_cards ?? 0);
  return `${used} of ${g.free_cards} free used`;
}

export default function MerchantDashboard() {
  const [me, setMe] = useState(null);
  const [wallet, setWallet] = useState({ balance: 0, currency: "USD" });
  const [counts, setCounts] = useState({ cards: 0, users: 0, tx: 0 });
  const [recent, setRecent] = useState([]);
  const location = useLocation();

  const loadDashboard = useCallback(() => {
    Promise.all([
      mApi.get("/merchant/me"),
      mApi.get("/merchant/wallet"),
      mApi.get("/merchant/cards?limit=1"),
      mApi.get("/merchant/users?limit=1"),
      merchantCardsApi.cardTransactions({ limit: 5 }),
    ]).then(([m, w, cs, us, tx]) => {
      setMe(m.data);
      setWallet(w.data);
      setCounts({ cards: cs.data.total, users: us.data.total, tx: tx.data.total ?? 0 });
      setRecent(tx.data.items || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, location.key]);

  useEffect(() => {
    const onFocus = () => loadDashboard();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadDashboard]);

  const merchant = me?.merchant;
  const user = mAuth.user();
  const grace = wallet.issuance_grace?.[0];
  const remainingFree = grace?.remaining_free_cards;

  return (
    <MerchantLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="m-dashboard">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Welcome back</div>
          <h1 className="font-display text-3xl tracking-tight font-medium">{user?.name || "Merchant"}</h1>
        </div>

        {merchant?.kyb_status !== "approved" && (
          <div className="mb-6 p-5 rounded-sm bg-amber-50 border border-amber-200 flex items-start gap-4">
            <FileArrowUp size={24} className="text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-display text-lg tracking-tight font-medium">Complete your KYB to go live</div>
              <p className="text-sm text-zinc-600 mt-1">
                Sandbox is fully unlocked. To enable production card issuance, upload your KYB documents.
                Status: <StatusPill value={merchant?.kyb_status || "pending"} />
              </p>
              <Link to={MERCHANT.kyb} data-testid="dash-kyb-cta" className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-zinc-950 underline-offset-4 hover:underline">
                Upload documents <ArrowUpRight size={12} />
              </Link>
            </div>
          </div>
        )}

        <div className={`grid grid-cols-1 sm:grid-cols-2 ${grace ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-4 mb-6`}>
          <Link to={MERCHANT.wallet} className="block">
            <Tile icon={Wallet} label="Wallet balance" value={fmt$(wallet.balance)} sub={`${wallet.currency} · view funding`} />
          </Link>
          {grace && (
            <Link to={MERCHANT.wallet} className="block" data-testid="dash-free-cards">
              <Tile
                icon={Gift}
                label="Free cards left"
                value={remainingFree}
                sub={graceUsageSubtext(grace)}
              />
            </Link>
          )}
          <Tile icon={CreditCard} label="Cards issued" value={counts.cards} sub="across cardholders" />
          <Tile icon={Users} label="Cardholders" value={counts.users} sub="created" />
          <Tile icon={Receipt} label="Transactions" value={counts.tx} sub="all-time" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl tracking-tight font-medium">Recent transactions</div>
              <Link to={MERCHANT.transactions} className="text-xs text-zinc-700 hover:text-zinc-950 inline-flex items-center gap-1">View all <ArrowUpRight size={12} /></Link>
            </div>
            {recent.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center">No transactions yet — start by issuing your first sandbox card.</div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {recent.map((t) => (
                  <li key={t.orderId || t.card_id} className="py-2.5 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium">{t.merchantName || t.bizDesc || t.cardholder_name}</div>
                      <div className="text-[10px] uppercase tracking-[0.05em] text-zinc-500">{t.masked_pan || t.cardholder_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-mono tabular-nums">{fmt$(t.final_price ?? t.platformPrincipal)}</div>
                      <StatusPill value={t.status_label || t.status} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm p-5">
            <div className="font-display text-xl tracking-tight font-medium mb-4">Quick start</div>
            <ul className="space-y-3 text-sm">
              <Step icon={Key} label="Create your first API key" to={MERCHANT.apiKeys} />
              <Step icon={ShieldCheck} label="Set IP whitelist" to={MERCHANT.security} />
              <Step icon={CreditCard} label="Issue a sandbox card" to={MERCHANT.cards} />
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="lg:col-span-2">
            <SecurityPosture />
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-5 flex flex-col">
            <div className="font-display text-xl tracking-tight font-medium mb-2">Webhook deliveries</div>
            <p className="text-sm text-zinc-600 mb-4">Inspect outbound webhook history and replay failed events.</p>
            <Link to={MERCHANT.webhookEvents} data-testid="dash-webhook-events-link" className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-zinc-950 hover:underline">
              Open delivery log <ArrowUpRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </MerchantLayout>
  );
}

function Tile({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.05em] font-semibold text-zinc-500">{label}</div>
          <div className="font-display text-3xl tracking-tight font-medium mt-2 tabular-nums">{value}</div>
          <div className="text-xs text-zinc-500 mt-1">{sub}</div>
        </div>
        <div className="h-8 w-8 rounded-sm bg-zinc-100 flex items-center justify-center">
          <Icon size={16} className="text-zinc-700" />
        </div>
      </div>
    </div>
  );
}

function Step({ icon: Icon, label, to }) {
  return (
    <li>
      <Link to={to} className="flex items-center gap-3 p-2 -m-2 rounded-sm hover:bg-zinc-50 transition-colors duration-150">
        <div className="h-7 w-7 rounded-sm bg-zinc-100 flex items-center justify-center"><Icon size={14} /></div>
        <span className="flex-1 text-zinc-900">{label}</span>
        <ArrowUpRight size={14} className="text-zinc-400" />
      </Link>
    </li>
  );
}
